import { useMemo } from 'react';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { APM } from '../../../shared/types/APM';
import {
  Setting,
  SettingCreateBody,
  SettingUpdateBody,
} from '../../../shared/types/Settings';
import { RootState } from '../../app/store';
import { setSnackbarForEndpoint } from '../../utils/snackbar';
import { sepAPI } from '../API';
import { DepartmentLookup, DepartmentLookupCreateBody, DepartmentLookupUpdateBody } from '../../../shared/types/DepartmentLookup';
import { NotificationTable} from '../../../shared/types/NotificationTable';
import { APILogEntries } from '../../../shared/types/APILogEntries';

// Pagination types
export interface PaginationParams {
  page: number;
  limit: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  requestMethodName?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  totalRecords: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

// Custom hook to get only SEP Banner settings
export function useGetSepBannerSettingsQuery() {
  const { data, ...rest } = useGetSettingsQuery();
  const sepBanners = useMemo(() => (data || []).filter((s: Setting) => s.section === 'SEP Banner'), [data]);
  return { data: sepBanners, ...rest };
}
export interface AppState {
  snackbar: SnackbarState | null;
}

const initialState: AppState = {
  snackbar: null,
};

export interface SnackbarState {
  text: string;
  type: 'success' | 'error';
}

/** A slice used for general application-level stored data */
export const appSlice = createSlice({
  name: 'app',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // Use the PayloadAction type to declare the contents of `action.payload`
    setSnackbar: (state, action: PayloadAction<SnackbarState | null>) => {
      state.snackbar = action.payload;
    },
  },
});

export const { setSnackbar } = appSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.app.value)`
export const selectSnackbar = (state: RootState): SnackbarState | null =>
  state.app.snackbar;

export default appSlice.reducer;

// Get all departments in DB
export const utilsSlice = sepAPI.injectEndpoints({
  endpoints: (builder) => ({
    getApm: builder.query<APM, number | string | null>({
      query: (id) => `apm/${id}`,
    }),
    getSettings: builder.query<Array<Setting>, void>({
      query: () => `settings`,
      providesTags: ['Settings'],
    }),
    insertSetting: builder.mutation<Setting, SettingCreateBody>({
      query: (body) => {
        return {
          url: `settings`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: ['Settings'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Setting is created successfully!',
          errorMessage: 'There was a problem creating your Setting.',
        });
      },
    }),
    updateSetting: builder.mutation<
      Setting,
      { id: number; body: SettingUpdateBody }
    >({
      query: (data) => {
        const { id, body } = data;
        return {
          url: `settings/${id}`,
          method: 'PATCH',
          body: body,
        };
      },
      invalidatesTags: ['Settings'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Setting is updated successfully!',
          errorMessage: 'There was a problem updating your Setting.',
        });
      },
    }),
    reOrderSetting: builder.mutation<
      Setting,
      { id: number; body: SettingUpdateBody }
    >({
      query: (data) => {
        const { id, body } = data;
        return {
          url: `settings/${id}`,
          method: 'PATCH',
          body: body,
        };
      },
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Setting is updated successfully!',
          errorMessage: 'There was a problem updating your Setting.',
        });
      },
    }),
    deleteSetting: builder.mutation<void, number>({
      query: (id) => {
        return {
          url: `settings/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Settings'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Setting is deleted successfully!',
          errorMessage: 'There was a problem deleting your Setting.',
        });
      },
    }),
    insertSepBanner: builder.mutation<Setting, SettingCreateBody>({
      query: (body) => {
        return {
          url: `settings`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: ['Settings'],
      onQueryStarted: (arg, api) => {
        const bannerTitle = (arg as any)?.title || 'SEP banner';
        setSnackbarForEndpoint(api, {
          successMessage: `SEP banner created successfully!`,
          errorMessage: 'There was a problem creating the SEP banner.',
        });
      },
    }),
    updateSepBanner: builder.mutation<
      Setting,
      { id: number; body: SettingUpdateBody; suppressSnackbar?: boolean }
    >({
      query: (data) => {
        const { id, body } = data;
        return {
          url: `settings/${id}`,
          method: 'PATCH',
          body: body,
        };
      },
      invalidatesTags: ['Settings'],
      onQueryStarted: (arg, api) => {
        if (!arg.suppressSnackbar) {
          const bannerTitle = (arg.body as any)?.title || 'SEP banner';
          setSnackbarForEndpoint(api, {
            successMessage: `SEP banner updated successfully!`,
            errorMessage: 'There was a problem updating the SEP banner.',
          });
        }
      },
    }),
    deleteSepBanner: builder.mutation<void, number>({
      query: (id) => {
        return {
          url: `settings/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Settings'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'SEP banner deleted successfully!',
          errorMessage: 'There was a problem deleting the SEP banner.',
        });
      },
    }),
    getNotificationLogs: builder.query<Array<NotificationTable>, void>({
      query: () => `notification-logs`,      
    }),
    getAPILogs: builder.query<PaginatedResponse<APILogEntries>, PaginationParams>({
      query: ({ page, limit, search, startDate, endDate, status, requestMethodName }) => {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (search) params.append('search', search);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (status) params.append('status', status);
        if (requestMethodName) params.append('requestMethodName', requestMethodName);
        return `api-logs?${params.toString()}`;
      },      
    }),
    getAPILogRequestMethods: builder.query<string[], void>({
      query: () => `api-logs/request-methods`,      
    }),
    getDepartmentLookups: builder.query<Array<DepartmentLookup>, void>({
      query: () => `department-lookup`,      
    }),
    insertDepartmentLookup: builder.mutation<DepartmentLookup, DepartmentLookupCreateBody>({
      query: (body) => {
        return {
          url: `department-lookup`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: ['DepartmentLookups'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Department created successfully!',
          errorMessage: 'There was a problem creating your department.',
        });
      },
    }),
    updateDepartmentLookup: builder.mutation<
      DepartmentLookup,
      { id: number; body: DepartmentLookupUpdateBody }
    >({
      query: (data) => {
        const { id, body } = data;
        return {
          url: `department-lookup/${id}`,
          method: 'PATCH',
          body: body,
        };
      },
      invalidatesTags: ['DepartmentLookups'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Department updated successfully!',
          errorMessage: 'There was a problem updating your department.',
        });
      },
    }),
    batchReplaceDepartmentLookup: builder.mutation<
      { status: number; message: string; updatedRecords: DepartmentLookup[] },
      { replacements: Array<{ id: number; column: string; newValue: string }> }
    >({
      query: (body) => {
        return {
          url: `department-lookup/batch-replace`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: ['DepartmentLookups'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: `${arg.replacements.length} record(s) updated successfully!`,
          errorMessage: 'There was a problem updating records.',
        });
      },
    }),
    deleteDepartmentLookups: builder.mutation<void, number>({
      query: (id) => {
        return {
          url: `department-lookup/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['DepartmentLookups'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Department deleted successfully!',
          errorMessage: 'There was a problem deleting the department.',
        });
      },
    }),
    getAdminActivities: builder.query<any, { offset?: number; limit?: number; userId?: string; trackableType?: string } | void>({
      query: (params) => {
        const { offset = 0, limit = 25, userId, trackableType } = params || {};
        const searchParams = new URLSearchParams();
        if (offset !== undefined) searchParams.append('offset', String(offset));
        if (limit !== undefined) searchParams.append('limit', String(limit));
        if (userId) searchParams.append('userId', userId);
        if (trackableType) searchParams.append('trackableType', trackableType);
        const queryString = searchParams.toString();
        return `admin-activities${queryString ? `?${queryString}` : ''}`;
      },
    }),
    getAdminActivityUsers: builder.query<Array<{ userID: string; authorName: string }>, void>({
      query: () => 'admin-activities/users',
    }),
    getAdminActivityTrackableTypes: builder.query<string[], void>({
      query: () => 'admin-activities/trackable-types',
    }),
    exportDepartmentLookups: builder.query<any, void>({
      query: () => ({
        url: `department-lookup/export/excel`,
        cache: 'no-cache',
        headers: {
          'content-type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseHandler: async (response: any) => {
          return window.URL.createObjectURL(await response.blob());
        },
      }),
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Department lookups exported successfully!',
          errorMessage: 'There was a problem exporting department lookups.',
        });
      },
    }),
  }),
});

export const {
  useLazyGetApmQuery,
  useGetApmQuery,
  useGetSettingsQuery,
  useInsertSettingMutation,
  useUpdateSettingMutation,
  useDeleteSettingMutation,
  useInsertSepBannerMutation,
  useUpdateSepBannerMutation,
  useDeleteSepBannerMutation,
  useReOrderSettingMutation,
  useGetDepartmentLookupsQuery,
  useDeleteDepartmentLookupsMutation,
  useInsertDepartmentLookupMutation,
  useUpdateDepartmentLookupMutation,
  useBatchReplaceDepartmentLookupMutation,
  useGetNotificationLogsQuery,
  useGetAdminActivitiesQuery,
  useGetAdminActivityUsersQuery,
  useGetAdminActivityTrackableTypesQuery,
  useLazyExportDepartmentLookupsQuery,
  useGetAPILogsQuery,
  useGetAPILogRequestMethodsQuery,
} = utilsSlice;
